import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Snackbar,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreditsEngineService, { toNumber } from '../services/CreditsEngineService';

/**
 * Modal de comparación/upgrade de planes, mostrado desde el chat (ver
 * UpgradePlanBanner.js). Estilo modelado sobre OnboardingDialog.js (fondo
 * oscuro, borde acentuado, título centrado con barra de acento, CTA
 * píldora) -- paleta del chat, distinta a la del panel admin.
 *
 * Reglas de negocio (ver plan de migración "Upgrade de plan en el chat con
 * cobro real vía Stripe"):
 *  - root: puede asignar cualquier plan a la cuenta sin pasar por Stripe.
 *  - admin del tenant: puede cambiar a un plan gratis directo; un plan
 *    pago SIEMPRE pasa por Stripe Checkout (el backend rechaza el intento
 *    de asignación directa).
 *  - usuario invitado sin rol: solo puede ver, no puede confirmar nada.
 */
const cardBaseSx = {
    p: 2.5,
    borderRadius: 3,
    backgroundColor: 'var(--wb-x-bg-1c1f26, #1c1f26)',
    border: '1px solid var(--wb-overlay-10)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
};

const ctaButtonSx = {
    mt: 2,
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: 'var(--wb-blue-google)',
    '&:hover': { backgroundColor: 'var(--wb-x-bg-1666ce, #1666ce)' },
    '&:disabled': { backgroundColor: 'var(--wb-overlay-08)', color: 'var(--wb-text-ghost)' }
};

const PlanComparisonModal = ({
    open,
    onClose,
    accountId,
    isTenantAdmin,
    isPlatformRoot,
    currentPlanId,
    hasActiveSubscription,
    onPlanChanged
}) => {
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pendingPlanId, setPendingPlanId] = useState(null);
    const [isPortalLoading, setIsPortalLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const loadPlans = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const items = await CreditsEngineService.listPlanCatalog();
            items.sort((a, b) => toNumber(a.monthly_price) - toNumber(b.monthly_price));
            setPlans(items);
        } catch (err) {
            setError(err.message || 'No fue posible obtener los planes disponibles.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            loadPlans();
        }
    }, [open, loadPlans]);

    const canConfirm = Boolean(isTenantAdmin || isPlatformRoot);

    const handleSwitchDirect = async (planId) => {
        try {
            setPendingPlanId(planId);
            await CreditsEngineService.switchToPlan({ accountId, planId });
            setSnackbar({ open: true, message: 'Plan actualizado correctamente.', severity: 'success' });
            onPlanChanged?.();
            onClose();
        } catch (err) {
            setSnackbar({ open: true, message: err.message || 'No se pudo cambiar de plan.', severity: 'error' });
        } finally {
            setPendingPlanId(null);
        }
    };

    const handleCheckout = async (planId) => {
        try {
            setPendingPlanId(planId);
            const baseUrl = `${window.location.origin}${window.location.pathname}`;
            const response = await CreditsEngineService.createCheckoutSession({
                accountId,
                planId,
                successUrl: `${baseUrl}?checkout=success`,
                cancelUrl: `${baseUrl}?checkout=cancel`
            });
            window.location.href = response.checkout_url;
        } catch (err) {
            setSnackbar({ open: true, message: err.message || 'No se pudo iniciar el pago.', severity: 'error' });
            setPendingPlanId(null);
        }
    };

    const handleManageBilling = async () => {
        try {
            setIsPortalLoading(true);
            const response = await CreditsEngineService.createPortalSession({
                accountId,
                returnUrl: window.location.href
            });
            window.location.href = response.portal_url;
        } catch (err) {
            setSnackbar({ open: true, message: err.message || 'No se pudo abrir el portal de facturación.', severity: 'error' });
            setIsPortalLoading(false);
        }
    };

    const renderCta = (plan) => {
        const isCurrent = plan.plan_id === currentPlanId;
        const isPending = pendingPlanId === plan.plan_id;
        const isPaidPlan = toNumber(plan.monthly_price) > 0;

        if (isCurrent) {
            return (
                <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label="Plan actual"
                    sx={{ mt: 2, backgroundColor: 'var(--wb-x-bg-22c55e29, rgba(34,197,94,0.16))', color: 'var(--wb-success)', fontWeight: 600 }}
                />
            );
        }

        if (!canConfirm) {
            return (
                <Typography variant="caption" sx={{ mt: 2, color: 'var(--wb-x-text-e2e8f099, rgba(226,232,240,0.6))' }}>
                    Solicita el upgrade a tu administrador.
                </Typography>
            );
        }

        if (isPlatformRoot) {
            return (
                <Button
                    fullWidth
                    variant="contained"
                    disabled={Boolean(pendingPlanId)}
                    onClick={() => handleSwitchDirect(plan.plan_id)}
                    sx={ctaButtonSx}
                >
                    {isPending ? <CircularProgress size={18} color="inherit" /> : 'Asignar este plan'}
                </Button>
            );
        }

        // admin del tenant
        if (!isPaidPlan) {
            return (
                <Button
                    fullWidth
                    variant="contained"
                    disabled={Boolean(pendingPlanId)}
                    onClick={() => handleSwitchDirect(plan.plan_id)}
                    sx={ctaButtonSx}
                >
                    {isPending ? <CircularProgress size={18} color="inherit" /> : 'Cambiar a Free'}
                </Button>
            );
        }

        return (
            <Button
                fullWidth
                variant="contained"
                disabled={Boolean(pendingPlanId)}
                onClick={() => handleCheckout(plan.plan_id)}
                sx={ctaButtonSx}
            >
                {isPending ? <CircularProgress size={18} color="inherit" /> : 'Actualizar y pagar'}
            </Button>
        );
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'var(--wb-bg-deep)',
                        border: '1px solid var(--wb-orange)',
                        borderRadius: 4,
                        color: 'var(--wb-text-strong)'
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: 'var(--wb-text-faint)' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--wb-text-strong)' }}>
                            Planes de WhisperBI
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--wb-orange)', mt: 1 }}>
                            Elige el plan que mejor se ajuste a tu equipo
                        </Typography>
                        <Box sx={{ height: 4, width: 64, backgroundColor: 'var(--wb-orange)', borderRadius: 2, mt: 2, mx: 'auto' }} />
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 3 }}>
                    {error && (
                        <Typography variant="body2" sx={{ color: 'var(--wb-danger)', mb: 2, textAlign: 'center' }}>
                            {error}
                        </Typography>
                    )}
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: 'var(--wb-blue-google)' }} />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {plans.map((plan) => {
                                const isCurrent = plan.plan_id === currentPlanId;
                                const isPaidPlan = toNumber(plan.monthly_price) > 0;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={plan.plan_id}>
                                        <Box sx={{ ...cardBaseSx, borderColor: isCurrent ? 'var(--wb-blue-google)' : 'var(--wb-overlay-10)' }}>
                                            <Typography variant="h6" sx={{ color: 'var(--wb-text-strong)', fontWeight: 600 }}>
                                                {plan.name || plan.plan_id}
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: 'var(--wb-text-strong)', mt: 1, fontWeight: 700 }}>
                                                {isPaidPlan ? `$${toNumber(plan.monthly_price).toFixed(2)}` : 'Gratis'}
                                                {isPaidPlan && (
                                                    <Typography component="span" variant="body2" sx={{ color: 'var(--wb-x-text-e2e8f099, rgba(226,232,240,0.6))' }}>
                                                        {' '}/ {plan.currency || 'USD'} mes
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Box sx={{ mt: 2, flexGrow: 1 }}>
                                                <Typography variant="body2" sx={{ color: 'var(--wb-text)', mb: 0.75 }}>
                                                    • {toNumber(plan.included_credits).toLocaleString()} créditos/mes
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'var(--wb-text)', mb: 0.75 }}>
                                                    • {plan.max_users != null ? `Hasta ${plan.max_users} usuarios` : 'Usuarios ilimitados'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'var(--wb-text)', mb: 0.75 }}>
                                                    • {plan.max_connections != null ? `Hasta ${plan.max_connections} conexiones de datos` : 'Conexiones ilimitadas'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'var(--wb-text)' }}>
                                                    • {plan.allow_overage ? 'Sobregiro permitido' : 'Sin sobregiro'}
                                                </Typography>
                                            </Box>
                                            {renderCta(plan)}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    {canConfirm && hasActiveSubscription && (
                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <Button
                                onClick={handleManageBilling}
                                disabled={isPortalLoading}
                                sx={{ color: 'var(--wb-blue)', textTransform: 'none' }}
                            >
                                {isPortalLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                Administrar facturación
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{
                    sx: {
                        backgroundColor: snackbar.severity === 'success' ? 'var(--wb-success-90)' : 'var(--wb-danger-95)'
                    }
                }}
            />
        </>
    );
};

export default PlanComparisonModal;
